import React, {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import {getAbsoluteSrc} from '../absolute-src.js';
import {
	useFrameForVolumeProp,
} from '../audio/use-audio-frame.js';
import {CompositionManager} from '../composition-context';
import {OFFTHREAD_VIDEO_CLASS_NAME} from '../default-css.js';
import {continueRender, delayRender} from '../delay-render.js';
import {truthy} from '../truthy.js';
import {random} from '../random.js';
import {SequenceContext} from '../SequenceContext.js';
import {useTimelinePosition} from '../timeline-position-state.js';
import {useCurrentFrame} from '../use-current-frame.js';
import {useUnsafeVideoConfig} from '../use-video-config.js';
import {evaluateVolume} from '../volume-prop.js';
import {getExpectedMediaFrameUncorrected} from './get-current-time.js';

const DEFAULT_IMAGE_FORMAT = 'jpeg';

export const OffthreadVideoForRendering = ({
	onError,
	volume: volumeProp,
	playbackRate,
	src,
	muted,
	imageFormat,
	allowAmplificationDuringRender,
	...props
}) => {
	const absoluteFrame = useTimelinePosition();
	const frame = useCurrentFrame();
	const volumePropsFrame = useFrameForVolumeProp();
	const videoConfig = useUnsafeVideoConfig();
	const sequenceContext = useContext(SequenceContext);
	const {registerAsset, unregisterAsset} = useContext(CompositionManager);

	// Holds the active delayRender handle so onLoad/onError can resolve it
	const renderHandleRef = useRef(null);

	if (!src) {
		throw new TypeError('No `src` was passed to <OffthreadVideo>.');
	}

	const id = useMemo(
		() =>
			`offthreadvideo-${random(src ?? '')}-${sequenceContext?.cumulatedFrom}-${
				sequenceContext?.relativeFrom
			}-${sequenceContext?.durationInFrames}`,
		[
			src,
			sequenceContext?.cumulatedFrom,
			sequenceContext?.relativeFrom,
			sequenceContext?.durationInFrames,
		]
	);

	if (!videoConfig) {
		throw new Error('No video config found');
	}

	const proxyPort = typeof window !== 'undefined' ? window.picmaker_proxyPort : undefined;

	const volume = evaluateVolume({
		volume: volumeProp,
		frame: volumePropsFrame,
		mediaVolume: 1,
		allowAmplificationDuringRender: allowAmplificationDuringRender ?? false,
	});

	useEffect(() => {
		if (!src || !window.picmaker_audioEnabled || muted || volume <= 0) return;

		registerAsset({
			type: 'video',
			src: getAbsoluteSrc(src),
			id,
			frame: absoluteFrame,
			volume,
			mediaFrame: frame,
			playbackRate: playbackRate ?? 1,
			allowAmplificationDuringRender: allowAmplificationDuringRender ?? false,
		});

		return () => unregisterAsset(id);
	}, [
		muted,
		src,
		registerAsset,
		id,
		unregisterAsset,
		volume,
		frame,
		absoluteFrame,
		playbackRate,
		allowAmplificationDuringRender,
	]);

	const currentTime = useMemo(() => {
		// frame is sequence-relative (0 = scene start).
		// startFrom is the video file trim-in point in frames (videoStart * fps).
		// Adding it gives the absolute file timestamp for the proxy.
		const startFromFrames = props.startFrom ?? 0;
		const rawTime = getExpectedMediaFrameUncorrected({
			frame,
			playbackRate: playbackRate || 1,
			startFrom: startFromFrames,
		}) / videoConfig.fps;
		// Clamp to 0 — never request a negative timestamp from the proxy
		return Math.max(0, rawTime);
	}, [frame, props.startFrom, playbackRate, videoConfig.fps]);

	// Don't block rendering for frames before this clip starts (negative frame = pre-scene)
	const isActive = frame >= 0;

	const actualSrc = useMemo(() => {
		if (!proxyPort) return null;
		return `http://localhost:${proxyPort}/proxy?src=${encodeURIComponent(
			getAbsoluteSrc(src)
		)}&time=${encodeURIComponent(currentTime)}&imageFormat=${
			imageFormat ?? DEFAULT_IMAGE_FORMAT
		}`;
	}, [proxyPort, currentTime, imageFormat, src]);

	// Block the renderer until the frame image loads or errors.
	// A new handle is created each time actualSrc changes (new frame = new proxy URL).
	// We MUST ensure window.ready never flickers to true between handle transitions.
	// Strategy: create the new handle BEFORE resolving the old one.
	useLayoutEffect(() => {
		if (!proxyPort || !isActive) {
			// Going inactive — resolve any pending handle
			if (renderHandleRef.current !== null) {
				continueRender(renderHandleRef.current);
				renderHandleRef.current = null;
			}
			return;
		}
		if (!actualSrc) return;

		// Create new handle FIRST (window.ready stays false)
		const newHandle = delayRender('OffthreadVideo: ' + src);

		// THEN resolve old handle — no gap where window.ready = true
		if (renderHandleRef.current !== null) {
			continueRender(renderHandleRef.current);
		}

		renderHandleRef.current = newHandle;

		return () => {
			// On unmount — resolve so we don't leak handles
			if (renderHandleRef.current === newHandle) {
				continueRender(newHandle);
				renderHandleRef.current = null;
			}
		};
	}, [actualSrc, proxyPort, src, isActive]);

	const onLoad = useCallback(() => {
		if (renderHandleRef.current !== null) {
			continueRender(renderHandleRef.current);
			renderHandleRef.current = null;
		}
	}, []);

	// Always unblock on error — a blank frame is better than a hung renderer.
	const onImgError = useCallback(() => {
		onError?.(new Error('Frame proxy request failed: ' + src));
		if (renderHandleRef.current !== null) {
			continueRender(renderHandleRef.current);
			renderHandleRef.current = null;
		}
	}, [onError, src]);

	const className = useMemo(() => {
		return [OFFTHREAD_VIDEO_CLASS_NAME, props.className]
			.filter(truthy)
			.join(' ');
	}, [props.className]);

	// Proxy not available — render a black placeholder without blocking delayRender
	if (!proxyPort) {
		return <div style={{ background: '#000', ...props.style }} />;
	}

	// Not yet in the active window — render nothing so no stale frame shows
	if (!isActive) {
		return null;
	}

	return (
		<img
			{...props}
			src={actualSrc}
			className={className}
			onLoad={onLoad}
			onError={onImgError}
		/>
	);
};
